import { ReactNode, useEffect, useState } from 'react';
import { PrinterIcon } from '@heroicons/react/24/solid';
import SharedBadge from '@/global/Badge/Badge';
import SharedButton from '@/global/Button/Button';
import './legal-document.css';

type SectionItem = {
  id: string;
  number: string;
  label: string;
};

type ContactCard = {
  title: string;
  email: string;
  phone: string;
};

type LegalDocumentLayoutProps = {
  heroTag: string;
  heroTitle: ReactNode;
  heroSubtitle: string;
  sections: SectionItem[];
  contact: ContactCard;
  onDownload?: () => void;
  children: ReactNode;
};

const LegalDocumentLayout = ({
  heroTag,
  heroTitle,
  heroSubtitle,
  sections,
  contact,
  onDownload,
  children,
}: LegalDocumentLayoutProps) => {
  const [activeSection, setActiveSection] = useState(sections[0]?.id ?? '');

  useEffect(() => {
    const observedSections = sections
      .map(section => document.getElementById(section.id))
      .filter((section): section is HTMLElement => section !== null);

    if (!observedSections.length) return;

    const observer = new IntersectionObserver(
      entries => {
        const visibleEntries = entries
          .filter(entry => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visibleEntries.length > 0) {
          setActiveSection(visibleEntries[0].target.id);
        }
      },
      {
        rootMargin: '-80px 0px -55% 0px',
        threshold: [0.2, 0.35, 0.5, 0.7],
      },
    );

    observedSections.forEach(section => observer.observe(section));

    return () => observer.disconnect();
  }, [sections]);

  return (
    <div className="mb-legal">
      <section className="mb-hero">
        <div className="mb-shell">
          <SharedBadge text={heroTag} variant="secondary" size="xs" className="mb-hero-tag" />
          <h1>{heroTitle}</h1>
          <p>{heroSubtitle}</p>
        </div>
      </section>

      <div className="mb-shell mb-body">
        <aside className="mb-sidebar">
          <div className="mb-sidebar-title">Contents</div>
          <ul className="mb-toc">
            {sections.map(section => (
              <li key={section.id}>
                <a href={`#${section.id}`} className={activeSection === section.id ? 'active' : ''}>
                  <span className="mb-toc-num">{section.number}</span>
                  {section.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="mb-contact-card">
            <h4>{contact.title}</h4>
            <p>
              <strong>Email</strong>
              <br />
              <a href={`mailto:${contact.email}`}>{contact.email}</a>
            </p>
            <p>
              <strong>Phone</strong>
              <br />
              {contact.phone}
            </p>
            {onDownload && (
              <SharedButton
                variant="secondaryOutline"
                size="sm"
                className="mb-download-btn"
                onClick={onDownload}
                title="Download PDF"
                iconLeft={<PrinterIcon className="w-4 h-4" />}
              >
                Download PDF
              </SharedButton>
            )}
          </div>
        </aside>

        <main className="mb-content" id="printer">
          {children}
        </main>
      </div>
    </div>
  );
};

export default LegalDocumentLayout;
